/**
 * Las cuentas como se hacen en el colegio, paso a paso.
 *
 * Una calculadora da el resultado; un maestro enseña un camino que sirve para
 * la cuenta siguiente. Esto es el camino: la suma en columna con lo que se
 * lleva, la resta «de ocho a trece van cinco», la multiplicación con sus filas
 * corridas y la división en su caja, con el resto debajo y la cifra que se
 * baja. El mismo formato que el cuaderno, porque lo que se enseña aquí tiene
 * que reconocerse en clase al día siguiente.
 *
 * Cada cuenta se convierte en una lista de **pasos**. Un paso es un dato, no un
 * texto: dice qué columna es, qué cifras se suman, cuánto se lleva y cómo queda
 * el cuadro después. El texto lo pone la interfaz, que es donde vive todo lo
 * que lee un niño y donde hay un guardián que lo vigila. Y como es un dato, el
 * mismo paso sirve para tres cosas distintas:
 *
 * - **Verlo hecho**: el ejemplo resuelto, paso a paso, antes de intentarlo.
 * - **Hacerlo con ayuda**: los primeros pasos se enseñan hechos y los últimos
 *   los contesta el niño. Con cada acierto, un paso menos de ayuda. Es el
 *   *desvanecimiento hacia atrás* de Renkl y Atkinson (2003): empezar por el
 *   final es lo que mejor funciona, porque el niño ve el camino entero antes
 *   de tener que andarlo.
 * - **Pistas**: en cada paso hay dos, de lo general a lo concreto, y la
 *   respuesta solo se enseña tras dos intentos. Koedinger y Aleven (2007)
 *   midieron que los alumnos saltan a la última pista para copiar; por eso
 *   aquí no está a demanda.
 *
 * La razón de fondo está en Atkinson, Derry, Renkl y Wortham (2000), la
 * revisión del programa de los ejemplos resueltos: para quien empieza, estudiar
 * un ejemplo enseña más que pelearse con el problema, y el ejemplo funciona
 * mejor si va seguido de un problema gemelo y si sus pasos llevan nombre.
 *
 * Todo lo de aquí es puro: números en texto entran, pasos salen.
 */

import { formatear, type Ejercicio } from './mates.ts'

// --- El cuadro: cómo queda la cuenta en el cuaderno ---------------------------

export interface Celda {
  readonly t: string
  /** Escrito en este paso: es lo que se resalta. */
  readonly nuevo?: boolean
  /** Está pero no importa ahora: un cero de relleno, una llevada ya usada. */
  readonly tenue?: boolean
}

export interface Fila {
  readonly papel: 'llevadas' | 'operando' | 'parcial' | 'resultado'
  /** El signo que va a la izquierda del segundo operando. */
  readonly signo?: string
  /** Alineadas a la derecha; tantas como `ancho`. */
  readonly celdas: readonly Celda[]
  /** Con la raya encima: es donde empieza el resultado. */
  readonly raya?: boolean
}

export type Cuadro =
  | { readonly tipo: 'nada' }
  | { readonly tipo: 'columna'; readonly ancho: number; readonly filas: readonly Fila[] }
  | {
      readonly tipo: 'caja'
      readonly dividendo: readonly Celda[]
      readonly divisor: readonly Celda[]
      readonly cociente: readonly Celda[]
      /** Cada resto en su fila, alineado bajo la cifra hasta la que se ha usado. */
      readonly restos: readonly (readonly Celda[])[]
    }
  | {
      readonly tipo: 'tabla'
      readonly tabla: number
      readonly hasta: number
      readonly pide: number
    }

const NADA: Cuadro = { tipo: 'nada' }

// --- Los pasos ----------------------------------------------------------------

interface Base {
  readonly cuadro: Cuadro
  /**
   * Lo que se contesta en este paso, si se pregunta algo. Sin respuesta, el
   * paso solo se enseña: «pongo las comas una debajo de otra».
   */
  readonly respuesta?: string
}

export type Paso =
  | (Base & { readonly tipo: 'alinear'; readonly ceros: boolean })
  | (Base & {
      readonly tipo: 'sumar-columna'
      readonly lugar: string
      readonly a: number
      readonly b: number
      readonly llevada: number
      readonly suma: number
      readonly escribe: number
      readonly seLleva: number
    })
  | (Base & {
      readonly tipo: 'restar-columna'
      readonly lugar: string
      readonly arriba: number
      readonly abajo: number
      readonly llevada: number
      /** Lo de abajo con lo que se llevaba: es lo que se resta de verdad. */
      readonly quita: number
      readonly puede: boolean
      /** Cuando no se puede, el número que se coge: 13 en vez de 3. */
      readonly tomo: number
      readonly resultado: number
      readonly seLleva: number
    })
  | (Base & { readonly tipo: 'bajar-coma' })
  | (Base & {
      readonly tipo: 'quitar-comas'
      readonly a: string
      readonly b: string
      readonly sinA: string
      readonly sinB: string
      readonly decimales: number
    })
  | (Base & {
      readonly tipo: 'multiplicar-cifra'
      readonly lugar: string
      readonly cifra: number
      readonly por: number
      readonly llevada: number
      readonly producto: number
      readonly escribe: number
      readonly seLleva: number
    })
  | (Base & {
      readonly tipo: 'nueva-fila'
      readonly cifra: number
      readonly lugar: string
      readonly desplaza: number
    })
  | (Base & {
      readonly tipo: 'sumar-filas'
      readonly sumandos: readonly string[]
      readonly total: string
    })
  | (Base & {
      readonly tipo: 'poner-coma'
      readonly decimales: number
      readonly sinComa: string
      readonly resultado: string
    })
  | (Base & {
      readonly tipo: 'div-quitar-coma'
      readonly por: number
      readonly a: string
      readonly b: string
      readonly nuevoA: string
      readonly nuevoB: string
    })
  | (Base & {
      readonly tipo: 'div-empezar'
      readonly divisor: number
      readonly numero: number
      readonly cifras: number
      /** El entero no llegaba: se escribe «0,» y se sigue con los decimales. */
      readonly ceroYComa: boolean
      readonly cerosAnadidos: number
    })
  | (Base & {
      readonly tipo: 'div-cabe'
      readonly divisor: number
      readonly en: number
      readonly veces: number
      readonly producto: number
      readonly siguiente: number
    })
  | (Base & {
      readonly tipo: 'div-resto'
      readonly en: number
      readonly producto: number
      readonly resto: number
      readonly baja?: string
      readonly queda?: number
    })
  | (Base & { readonly tipo: 'div-coma' })
  | (Base & { readonly tipo: 'div-cero'; readonly conComa: boolean })
  | (Base & { readonly tipo: 'div-fin'; readonly cociente: string; readonly resto: number })
  | (Base & {
      readonly tipo: 'tabla'
      readonly tabla: number
      readonly por: number
      readonly anterior: number
      readonly resultado: number
    })
  | (Base & { readonly tipo: 'traducir'; readonly de: string; readonly a: string })
  | (Base & {
      readonly tipo: 'producto'
      readonly a: number
      readonly b: number
      readonly resultado: number
    })
  | (Base & {
      readonly tipo: 'mover-coma'
      readonly numero: string
      readonly sitios: number
      readonly resultado: string
    })
  | (Base & {
      readonly tipo: 'coger'
      readonly partes: number
      readonly cada: number
      readonly resultado: number
    })
  | (Base & {
      readonly tipo: 'primero'
      readonly que: 'parentesis' | 'multiplicacion'
      readonly cuenta: string
      readonly resultado: number
    })
  | (Base & { readonly tipo: 'luego'; readonly cuenta: string; readonly resultado: number })
  | (Base & {
      readonly tipo: 'subir'
      readonly desde: number
      readonly sube: number
      readonly hastaCero: number
      readonly resultado: number
    })
  | (Base & {
      readonly tipo: 'redondear'
      readonly numero: string
      readonly mira: number
      readonly sube: boolean
      readonly resultado: string
    })

export interface Traza {
  readonly pasos: readonly Paso[]
  /** A lo que llegan los pasos. Tiene que ser la solución del ejercicio. */
  readonly solucion: string
}

/** Cuántos pasos piden algo: es lo que se puede ir dejando al niño. */
export function preguntasDe(traza: Traza): number {
  return traza.pasos.filter((p) => p.respuesta !== undefined).length
}

// --- Números en texto ---------------------------------------------------------

/**
 * Un número tal como se escribe: sus cifras seguidas y cuántas son decimales.
 * `12,50` son las cifras `1250` con dos decimales. Nada de coma flotante: las
 * cuentas de aquí se hacen cifra a cifra, que es justo lo que se enseña.
 */
interface Num {
  readonly cifras: string
  readonly decimales: number
}

function leerNum(texto: string): Num {
  const [entera = '0', decimal = ''] = texto.trim().split(',')
  const entero = entera.replace(/^0+(?=\d)/, '') || '0'
  return { cifras: entero + decimal, decimales: decimal.length }
}

function conDecimales(n: Num, decimales: number): Num {
  return { cifras: n.cifras + '0'.repeat(decimales - n.decimales), decimales }
}

function textoDe(n: Num): string {
  if (n.decimales === 0) return n.cifras
  const cifras = n.cifras.padStart(n.decimales + 1, '0')
  return `${cifras.slice(0, -n.decimales)},${cifras.slice(-n.decimales)}`
}

function valorDe(n: Num): number {
  return Number(n.cifras)
}

/** La cifra que ocupa el lugar `k` contando desde la derecha; cero si no hay. */
function cifraDesdeDerecha(n: Num, k: number): number {
  const i = n.cifras.length - 1 - k
  return i < 0 ? 0 : Number(n.cifras[i] ?? '0')
}

const ENTEROS = [
  'unidades',
  'decenas',
  'centenas',
  'unidades de millar',
  'decenas de millar',
  'centenas de millar',
  'millones',
]
const DECIMALES = ['décimas', 'centésimas', 'milésimas']

/** Cómo se llama la columna `k` desde la derecha en un número con `d` decimales. */
function lugar(k: number, d: number): string {
  if (k < d) return DECIMALES[d - 1 - k] ?? 'decimales'
  return ENTEROS[k - d] ?? 'la siguiente columna'
}

// --- El cuadro en columna, mutable mientras se construye ----------------------

interface Rejilla {
  ancho: number
  filas: { papel: Fila['papel']; signo?: string; celdas: Celda[]; raya?: boolean }[]
}

function vacias(ancho: number): Celda[] {
  return Array.from({ length: ancho }, () => ({ t: '' }))
}

/** La columna (desde la izquierda) de la cifra `k` (desde la derecha). */
function columnaDe(ancho: number, k: number, d: number): number {
  const desdeDerecha = d > 0 && k >= d ? k + 1 : k
  return ancho - 1 - desdeDerecha
}

function filaDe(
  ancho: number,
  n: Num,
  papel: Fila['papel'],
  signo?: string,
): Rejilla['filas'][number] {
  const celdas = vacias(ancho)
  const texto = textoDe(n)
  for (let i = 0; i < texto.length; i += 1) {
    celdas[ancho - texto.length + i] = { t: texto[i] ?? '' }
  }
  return signo === undefined ? { papel, celdas } : { papel, signo, celdas }
}

/** Una foto del cuadro, y se apaga lo nuevo para el paso siguiente. */
function foto(rejilla: Rejilla): Cuadro {
  const cuadro: Cuadro = {
    tipo: 'columna',
    ancho: rejilla.ancho,
    filas: rejilla.filas.map((fila) => ({
      ...fila,
      celdas: fila.celdas.map((c) => ({ ...c })),
    })),
  }
  for (const fila of rejilla.filas) {
    for (let i = 0; i < fila.celdas.length; i += 1) {
      const celda = fila.celdas[i]
      if (celda?.nuevo) fila.celdas[i] = { t: celda.t, ...(celda.tenue ? { tenue: true } : {}) }
    }
  }
  return cuadro
}

function escribir(fila: { celdas: Celda[] }, col: number, texto: string, tenue = false): void {
  // Un número de varias cifras se escribe hacia la izquierda desde `col`.
  for (let i = 0; i < texto.length; i += 1) {
    const c = col - (texto.length - 1) + i
    if (c >= 0)
      fila.celdas[c] = { t: texto[i] ?? '', nuevo: true, ...(tenue ? { tenue: true } : {}) }
  }
}

// --- Sumar en columna ---------------------------------------------------------

function sumar(aTexto: string, bTexto: string): Traza {
  const a0 = leerNum(aTexto)
  const b0 = leerNum(bTexto)
  const d = Math.max(a0.decimales, b0.decimales)
  const a = conDecimales(a0, d)
  const b = conDecimales(b0, d)
  const cifras = Math.max(a.cifras.length, b.cifras.length)
  const ancho = cifras + 1 + (d > 0 ? 1 : 0)

  const rejilla: Rejilla = {
    ancho,
    filas: [
      { papel: 'llevadas', celdas: vacias(ancho) },
      filaDe(ancho, a, 'operando'),
      filaDe(ancho, b, 'operando', '+'),
      { papel: 'resultado', celdas: vacias(ancho), raya: true },
    ],
  }
  const [llevadas, , , resultado] = rejilla.filas
  if (!llevadas || !resultado) throw new Error('rejilla incompleta')

  const pasos: Paso[] = []
  if (d > 0) {
    pasos.push({ tipo: 'alinear', ceros: a0.decimales !== b0.decimales, cuadro: foto(rejilla) })
  }

  let llevada = 0
  for (let k = 0; k < cifras; k += 1) {
    const ca = cifraDesdeDerecha(a, k)
    const cb = cifraDesdeDerecha(b, k)
    const suma = ca + cb + llevada
    const ultima = k === cifras - 1
    const escribe = ultima ? suma : suma % 10
    const seLleva = ultima ? 0 : Math.floor(suma / 10)
    escribir(resultado, columnaDe(ancho, k, d), String(escribe))
    if (seLleva > 0) escribir(llevadas, columnaDe(ancho, k + 1, d), String(seLleva))
    pasos.push({
      tipo: 'sumar-columna',
      lugar: lugar(k, d),
      a: ca,
      b: cb,
      llevada,
      suma,
      escribe,
      seLleva,
      respuesta: String(suma),
      cuadro: foto(rejilla),
    })
    llevada = seLleva
    if (d > 0 && k === d - 1) {
      escribir(resultado, ancho - 1 - d, ',')
      pasos.push({ tipo: 'bajar-coma', cuadro: foto(rejilla) })
    }
  }

  const total: Num = { cifras: String(valorDe(a) + valorDe(b)), decimales: d }
  return { pasos, solucion: formatear(aMilesimas(total)) }
}

function aMilesimas(n: Num): number {
  return valorDe(n) * 10 ** (3 - n.decimales)
}

// --- Restar en columna --------------------------------------------------------

/**
 * La resta «llevándose una» como se enseña en España: cuando no se puede, se
 * cogen diez más arriba y se le añade uno al número de abajo en la columna
 * siguiente. No es el único método —en otros países se tacha la cifra de
 * arriba— pero es el del cuaderno de estos niños, y el que un maestro de aquí
 * reconoce.
 */
function restar(aTexto: string, bTexto: string): Traza {
  const a0 = leerNum(aTexto)
  const b0 = leerNum(bTexto)
  const d = Math.max(a0.decimales, b0.decimales)
  const a = conDecimales(a0, d)
  const b = conDecimales(b0, d)
  const cifras = Math.max(a.cifras.length, b.cifras.length)
  const ancho = cifras + 1 + (d > 0 ? 1 : 0)

  const rejilla: Rejilla = {
    ancho,
    filas: [
      { papel: 'llevadas', celdas: vacias(ancho) },
      filaDe(ancho, a, 'operando'),
      filaDe(ancho, b, 'operando', '−'),
      { papel: 'resultado', celdas: vacias(ancho), raya: true },
    ],
  }
  const [llevadas, , , resultado] = rejilla.filas
  if (!llevadas || !resultado) throw new Error('rejilla incompleta')

  const pasos: Paso[] = []
  if (d > 0) {
    pasos.push({ tipo: 'alinear', ceros: a0.decimales !== b0.decimales, cuadro: foto(rejilla) })
  }

  let llevada = 0
  for (let k = 0; k < cifras; k += 1) {
    const arriba = cifraDesdeDerecha(a, k)
    const abajo = cifraDesdeDerecha(b, k)
    const quita = abajo + llevada
    const puede = arriba >= quita
    const tomo = puede ? arriba : arriba + 10
    const res = tomo - quita
    const seLleva = puede ? 0 : 1
    escribir(resultado, columnaDe(ancho, k, d), String(res))
    if (seLleva > 0) escribir(llevadas, columnaDe(ancho, k + 1, d), '1')
    pasos.push({
      tipo: 'restar-columna',
      lugar: lugar(k, d),
      arriba,
      abajo,
      llevada,
      quita,
      puede,
      tomo,
      resultado: res,
      seLleva,
      respuesta: String(res),
      cuadro: foto(rejilla),
    })
    llevada = seLleva
    if (d > 0 && k === d - 1) {
      escribir(resultado, ancho - 1 - d, ',')
      pasos.push({ tipo: 'bajar-coma', cuadro: foto(rejilla) })
    }
  }

  const total: Num = { cifras: String(valorDe(a) - valorDe(b)), decimales: d }
  return { pasos, solucion: formatear(aMilesimas(total)) }
}

// --- Multiplicar en columna ---------------------------------------------------

function multiplicar(aTexto: string, bTexto: string): Traza {
  const a0 = leerNum(aTexto)
  const b0 = leerNum(bTexto)
  const decimales = a0.decimales + b0.decimales
  const A = a0.cifras.replace(/^0+(?=\d)/, '')
  const B = b0.cifras.replace(/^0+(?=\d)/, '')
  const na = A.length
  const nb = B.length
  const ancho = na + nb + 1

  const rejilla: Rejilla = {
    ancho,
    filas: [
      { papel: 'llevadas', celdas: vacias(ancho) },
      filaDe(ancho, { cifras: A, decimales: 0 }, 'operando'),
      filaDe(ancho, { cifras: B, decimales: 0 }, 'operando', '×'),
    ],
  }
  const llevadas = rejilla.filas[0]
  if (!llevadas) throw new Error('rejilla incompleta')

  const pasos: Paso[] = []
  if (decimales > 0) {
    pasos.push({
      tipo: 'quitar-comas',
      a: aTexto,
      b: bTexto,
      sinA: A,
      sinB: B,
      decimales,
      cuadro: foto(rejilla),
    })
  }

  const sumandos: string[] = []
  for (let j = 0; j < nb; j += 1) {
    const cifra = Number(B[nb - 1 - j] ?? '0')
    const fila = {
      papel: nb === 1 ? ('resultado' as const) : ('parcial' as const),
      celdas: vacias(ancho),
      raya: j === 0,
    }
    rejilla.filas.push(fila)
    for (let i = 0; i < llevadas.celdas.length; i += 1) llevadas.celdas[i] = { t: '' }

    if (j > 0) {
      pasos.push({
        tipo: 'nueva-fila',
        cifra,
        lugar: lugar(j, 0),
        desplaza: j,
        cuadro: foto(rejilla),
      })
    }
    let llevada = 0
    for (let i = 0; i < na; i += 1) {
      const por = Number(A[na - 1 - i] ?? '0')
      const producto = cifra * por + llevada
      const ultima = i === na - 1
      const escribe = ultima ? producto : producto % 10
      const seLleva = ultima ? 0 : Math.floor(producto / 10)
      escribir(fila, ancho - 1 - j - i, String(escribe))
      if (seLleva > 0) escribir(llevadas, ancho - 1 - j - i - 1, String(seLleva))
      pasos.push({
        tipo: 'multiplicar-cifra',
        lugar: lugar(i, 0),
        cifra,
        por,
        llevada,
        producto,
        escribe,
        seLleva,
        respuesta: String(producto),
        cuadro: foto(rejilla),
      })
      llevada = seLleva
    }
    sumandos.push(String(Number(A) * cifra * 10 ** j))
  }

  const total = Number(A) * Number(B)
  if (nb > 1) {
    const fila = { papel: 'resultado' as const, celdas: vacias(ancho), raya: true }
    rejilla.filas.push(fila)
    escribir(fila, ancho - 1, String(total))
    pasos.push({
      tipo: 'sumar-filas',
      sumandos,
      total: String(total),
      respuesta: String(total),
      cuadro: foto(rejilla),
    })
  }

  const solucion = formatear(total * 10 ** (3 - decimales))
  if (decimales > 0) {
    pasos.push({
      tipo: 'poner-coma',
      decimales,
      sinComa: String(total),
      resultado: solucion,
      respuesta: solucion,
      cuadro: foto(rejilla),
    })
  }
  return { pasos, solucion }
}

// --- Dividir en la caja -------------------------------------------------------

interface Caja {
  dividendo: Celda[]
  divisor: Celda[]
  cociente: Celda[]
  restos: Celda[][]
}

function fotoCaja(caja: Caja): Cuadro {
  const copia = (celdas: Celda[]): Celda[] => celdas.map((c) => ({ ...c }))
  const cuadro: Cuadro = {
    tipo: 'caja',
    dividendo: copia(caja.dividendo),
    divisor: copia(caja.divisor),
    cociente: copia(caja.cociente),
    restos: caja.restos.map(copia),
  }
  const apagar = (celdas: Celda[]): void => {
    for (let i = 0; i < celdas.length; i += 1) {
      const c = celdas[i]
      if (c?.nuevo) celdas[i] = { t: c.t, ...(c.tenue ? { tenue: true } : {}) }
    }
  }
  apagar(caja.dividendo)
  apagar(caja.divisor)
  apagar(caja.cociente)
  caja.restos.forEach(apagar)
  return cuadro
}

/** Hasta tres decimales en el cociente: más que eso ya no es de primaria. */
const DECIMALES_MAXIMOS = 3

function dividir(aTexto: string, bTexto: string): Traza {
  const pasos: Paso[] = []
  let a = leerNum(aTexto)
  let b = leerNum(bTexto)

  const caja: Caja = { dividendo: [], divisor: [], cociente: [], restos: [] }
  const pintarDividendo = (n: Num): void => {
    caja.dividendo = textoDe(n)
      .split('')
      .map((t) => ({ t }))
  }

  // Un divisor con coma no se puede usar en la caja: se corre la coma de los
  // dos el mismo número de sitios, que es multiplicarlos por diez o por cien.
  if (b.decimales > 0) {
    const por = 10 ** b.decimales
    const nuevoA: Num =
      a.decimales > b.decimales
        ? { cifras: a.cifras, decimales: a.decimales - b.decimales }
        : { cifras: conDecimales(a, b.decimales).cifras, decimales: 0 }
    const nuevoB: Num = { cifras: b.cifras.replace(/^0+(?=\d)/, ''), decimales: 0 }
    a = { cifras: nuevoA.cifras.replace(/^0+(?=\d)/, '') || '0', decimales: nuevoA.decimales }
    b = nuevoB
    pintarDividendo(a)
    caja.divisor = textoDe(b)
      .split('')
      .map((t) => ({ t, nuevo: true }))
    pasos.push({
      tipo: 'div-quitar-coma',
      por,
      a: aTexto,
      b: bTexto,
      nuevoA: textoDe(a),
      nuevoB: textoDe(b),
      cuadro: fotoCaja(caja),
    })
  } else {
    pintarDividendo(a)
    caja.divisor = textoDe(b)
      .split('')
      .map((t) => ({ t }))
  }

  const divisor = valorDe(b)
  const enteras = a.cifras.length - a.decimales
  /** La columna del cuadro donde está la cifra `i` del dividendo (desde la izquierda). */
  const columna = (i: number): number => (a.decimales > 0 && i >= enteras ? i + 1 : i)

  // Con cuántas cifras se empieza: las justas para que quepa el divisor.
  let cifras = 0
  let numero = 0
  while (cifras < enteras && (numero < divisor || cifras === 0)) {
    numero = numero * 10 + Number(a.cifras[cifras] ?? '0')
    cifras += 1
  }
  let ceroYComa = false
  let cerosAnadidos = 0
  let comaPuesta = false
  let decimalesCociente = 0
  if (numero < divisor) {
    // El entero no llega: «0,» en el cociente y se sigue con los decimales,
    // añadiendo ceros al dividendo si hacen falta.
    ceroYComa = true
    comaPuesta = true
    caja.cociente.push({ t: '0', nuevo: true }, { t: ',', nuevo: true })
    while (numero < divisor && decimalesCociente < DECIMALES_MAXIMOS) {
      if (cifras < a.cifras.length) {
        numero = numero * 10 + Number(a.cifras[cifras] ?? '0')
      } else {
        numero *= 10
        cerosAnadidos += 1
        a = { cifras: `${a.cifras}0`, decimales: a.decimales + 1 }
        caja.dividendo.push({ t: '0', nuevo: true, tenue: true })
        if (a.decimales === 1) caja.dividendo.splice(enteras, 0, { t: ',', nuevo: true })
      }
      cifras += 1
      decimalesCociente += 1
      if (numero < divisor) caja.cociente.push({ t: '0', nuevo: true })
    }
  }
  for (let i = 0; i < cifras; i += 1) {
    const c = caja.dividendo[columna(i)]
    if (c) caja.dividendo[columna(i)] = { ...c, nuevo: true }
  }
  pasos.push({
    tipo: 'div-empezar',
    divisor,
    numero,
    cifras,
    ceroYComa,
    cerosAnadidos,
    respuesta: String(numero),
    cuadro: fotoCaja(caja),
  })

  let resto = numero
  let pos = cifras
  let cocienteTexto = ceroYComa ? '0,' : ''
  cocienteTexto += '0'.repeat(Math.max(0, decimalesCociente - 1))

  for (let vuelta = 0; vuelta < 12; vuelta += 1) {
    const veces = Math.floor(resto / divisor)
    const producto = veces * divisor
    caja.cociente.push({ t: String(veces), nuevo: true })
    cocienteTexto += String(veces)
    pasos.push({
      tipo: 'div-cabe',
      divisor,
      en: resto,
      veces,
      producto,
      siguiente: (veces + 1) * divisor,
      respuesta: String(veces),
      cuadro: fotoCaja(caja),
    })

    const r = resto - producto
    const fila = (texto: string, hasta: number, nuevo: boolean): Celda[] => {
      const celdas: Celda[] = vacias(hasta + 1)
      for (let i = 0; i < texto.length; i += 1) {
        celdas[hasta - (texto.length - 1) + i] = { t: texto[i] ?? '', nuevo }
      }
      return celdas
    }

    if (pos < a.cifras.length) {
      if (a.decimales > 0 && pos === enteras && !comaPuesta) {
        comaPuesta = true
        caja.cociente.push({ t: ',', nuevo: true })
        cocienteTexto += ','
        pasos.push({ tipo: 'div-coma', cuadro: fotoCaja(caja) })
      }
      const baja = a.cifras[pos] ?? '0'
      const queda = r * 10 + Number(baja)
      caja.restos.push(fila(`${String(r)}${baja}`, columna(pos), true))
      pos += 1
      pasos.push({
        tipo: 'div-resto',
        en: resto,
        producto,
        resto: r,
        baja,
        queda,
        respuesta: String(queda),
        cuadro: fotoCaja(caja),
      })
      resto = queda
      continue
    }

    caja.restos.push(fila(String(r), columna(pos - 1), true))
    pasos.push({
      tipo: 'div-resto',
      en: resto,
      producto,
      resto: r,
      respuesta: String(r),
      cuadro: fotoCaja(caja),
    })
    resto = r

    if (r > 0 && decimalesCociente < DECIMALES_MAXIMOS) {
      // No quedan cifras y sobra resto: se pone la coma y se baja un cero.
      const conComa = !comaPuesta
      if (conComa) {
        comaPuesta = true
        caja.cociente.push({ t: ',', nuevo: true })
        cocienteTexto += ','
        if (a.decimales === 0) caja.dividendo.push({ t: ',', nuevo: true, tenue: true })
      }
      a = { cifras: `${a.cifras}0`, decimales: a.decimales + 1 }
      caja.dividendo.push({ t: '0', nuevo: true, tenue: true })
      const ultimo = caja.restos[caja.restos.length - 1]
      if (ultimo) ultimo.push({ t: '0', nuevo: true, tenue: true })
      decimalesCociente += 1
      pasos.push({ tipo: 'div-cero', conComa, cuadro: fotoCaja(caja) })
      resto = r * 10
      pos += 1
      continue
    }

    pasos.push({ tipo: 'div-fin', cociente: cocienteTexto, resto: r, cuadro: fotoCaja(caja) })
    break
  }

  const solucion = formatear(aMilesimas(leerNum(cocienteTexto)))
  return { pasos, solucion }
}

// --- Las que no son de columna ------------------------------------------------

function tabla(tablaDe: number, por: number): Traza {
  const resultado = tablaDe * por
  return {
    pasos: [
      {
        tipo: 'tabla',
        tabla: tablaDe,
        por,
        anterior: tablaDe * (por - 1),
        resultado,
        respuesta: String(resultado),
        cuadro: { tipo: 'tabla', tabla: tablaDe, hasta: por - 1, pide: por },
      },
    ],
    solucion: String(resultado),
  }
}

function producto(a: number, b: number): Paso {
  return { tipo: 'producto', a, b, resultado: a * b, respuesta: String(a * b), cuadro: NADA }
}

function traducir(de: string, a: string): Paso {
  return { tipo: 'traducir', de, a, cuadro: NADA }
}

const PARTES: Readonly<Record<string, number>> = { 'la mitad': 2, 'un tercio': 3, 'un cuarto': 4 }

/**
 * Los pasos de un ejercicio, o nada si esta cuenta no tiene un camino que
 * enseñar. Lee el enunciado tal como se le enseña al niño: así lo que se
 * explica es exactamente lo que se ha preguntado.
 */
export function trazar(ejercicio: Ejercicio): Traza | undefined {
  const { destreza, pide } = ejercicio
  const binaria = /^(-?[\d,]+) ([+\-×:]) ([\d,]+)$/.exec(pide)

  if (binaria) {
    const [, a = '', op = '', b = ''] = binaria
    if (op === '+' && !a.startsWith('-')) return sumar(a, b)
    if (op === '-') return restar(a, b)
    if (op === '×') {
      const tablaDe = /^tabla-(\d+)$/.exec(destreza)
      return tablaDe ? tabla(Number(a), Number(b)) : multiplicar(a, b)
    }
    if (op === ':') return dividir(a, b)
    if (op === '+' && a.startsWith('-')) {
      const desde = Number(a)
      const sube = Number(b)
      const resultado = desde + sube
      return {
        pasos: [
          {
            tipo: 'subir',
            desde,
            sube,
            hastaCero: -desde,
            resultado,
            respuesta: String(resultado),
            cuadro: NADA,
          },
        ],
        solucion: String(resultado),
      }
    }
  }

  const parte = /^(la mitad|un tercio|un cuarto) de (\d+)$/.exec(pide)
  if (parte) {
    const entre = PARTES[parte[1] ?? ''] ?? 2
    const total = parte[2] ?? '0'
    const division = dividir(total, String(entre))
    return {
      pasos: [traducir(pide, `${total} : ${String(entre)}`), ...division.pasos],
      solucion: division.solucion,
    }
  }

  const fraccion = /^(\d+)\/(\d+) de (\d+)$/.exec(pide)
  if (fraccion) {
    const partes = Number(fraccion[1])
    const entre = fraccion[2] ?? '1'
    const total = fraccion[3] ?? '0'
    const division = dividir(total, entre)
    const cada = Number(division.solucion)
    const resultado = partes * cada
    return {
      pasos: [
        traducir(pide, `${total} : ${entre}, y lo que salga × ${String(partes)}`),
        ...division.pasos,
        { tipo: 'coger', partes, cada, resultado, respuesta: String(resultado), cuadro: NADA },
      ],
      solucion: String(resultado),
    }
  }

  const porciento = /^(\d+)% de (\d+)$/.exec(pide)
  if (porciento) {
    const tanto = porciento[1] ?? '0'
    const base = porciento[2] ?? '0'
    const multi = multiplicar(base, tanto)
    const resultado = formatear(Number(multi.solucion) * 10)
    return {
      pasos: [
        traducir(pide, `${base} × ${tanto} : 100`),
        ...multi.pasos,
        {
          tipo: 'mover-coma',
          numero: multi.solucion,
          sitios: 2,
          resultado,
          respuesta: resultado,
          cuadro: NADA,
        },
      ],
      solucion: resultado,
    }
  }

  const potencia = /^(\d+)([²³])$/.exec(pide)
  if (potencia) {
    const base = Number(potencia[1])
    const cubo = potencia[2] === '³'
    const cuadrado = base * base
    const pasos: Paso[] = [
      traducir(
        pide,
        cubo
          ? `${String(base)} × ${String(base)} × ${String(base)}`
          : `${String(base)} × ${String(base)}`,
      ),
      producto(base, base),
    ]
    if (cubo) pasos.push(producto(cuadrado, base))
    const resultado = cubo ? cuadrado * base : cuadrado
    return { pasos, solucion: String(resultado) }
  }

  const multiplo = /^el múltiplo de (\d+) que hace (\d+)$/.exec(pide)
  if (multiplo) {
    const de = Number(multiplo[1])
    const veces = Number(multiplo[2])
    return {
      pasos: [traducir(pide, `${String(de)} × ${String(veces)}`), producto(de, veces)],
      solucion: String(de * veces),
    }
  }

  const enDecimal = /^(\d+)\/(\d+) en decimal$/.exec(pide)
  if (enDecimal) {
    const division = dividir(enDecimal[1] ?? '1', enDecimal[2] ?? '1')
    return {
      pasos: [traducir(pide, `${enDecimal[1] ?? ''} : ${enDecimal[2] ?? ''}`), ...division.pasos],
      solucion: division.solucion,
    }
  }

  const redondeo = /^redondea ([\d,]+) a la décima$/.exec(pide)
  if (redondeo) {
    const numero = redondeo[1] ?? '0'
    const n = leerNum(numero)
    const centesimas = cifraDesdeDerecha(n, n.decimales - 2)
    const sube = centesimas >= 5
    const decimas = Math.round(aMilesimas(n) / 100) * 100
    const resultado = formatear(decimas)
    return {
      pasos: [
        {
          tipo: 'redondear',
          numero,
          mira: centesimas,
          sube,
          resultado,
          respuesta: resultado,
          cuadro: NADA,
        },
      ],
      solucion: resultado,
    }
  }

  const combinada = /^(\d+) \+ (\d+) × (\d+)$/.exec(pide)
  if (combinada) {
    const [a, b, c] = [Number(combinada[1]), Number(combinada[2]), Number(combinada[3])]
    const primero = b * c
    const total = a + primero
    return {
      pasos: [
        {
          tipo: 'primero',
          que: 'multiplicacion',
          cuenta: `${String(b)} × ${String(c)}`,
          resultado: primero,
          respuesta: String(primero),
          cuadro: NADA,
        },
        {
          tipo: 'luego',
          cuenta: `${String(a)} + ${String(primero)}`,
          resultado: total,
          respuesta: String(total),
          cuadro: NADA,
        },
      ],
      solucion: String(total),
    }
  }

  const parentesis = /^\((\d+) \+ (\d+)\) × (\d+)$/.exec(pide)
  if (parentesis) {
    const [a, b, c] = [Number(parentesis[1]), Number(parentesis[2]), Number(parentesis[3])]
    const primero = a + b
    const total = primero * c
    return {
      pasos: [
        {
          tipo: 'primero',
          que: 'parentesis',
          cuenta: `${String(a)} + ${String(b)}`,
          resultado: primero,
          respuesta: String(primero),
          cuadro: NADA,
        },
        {
          tipo: 'luego',
          cuenta: `${String(primero)} × ${String(c)}`,
          resultado: total,
          respuesta: String(total),
          cuadro: NADA,
        },
      ],
      solucion: String(total),
    }
  }

  return undefined
}

// --- Cuánta ayuda toca --------------------------------------------------------

export interface Ayuda {
  /** Verlo hecho antes de intentarlo: la primera vez tras un fallo. */
  readonly ejemploAntes: boolean
  /** Cuántos de los pasos que preguntan hace el niño, empezando por el final. */
  readonly pasosQueHace: number | 'todos'
}

/** Cuántos intentos de atrás se miran. Lo que pasó hace un mes ya no dice cómo va. */
const VENTANA = 6

/**
 * Cuánta ayuda toca en una destreza, mirando sus últimos intentos.
 *
 * La regla es la del desvanecimiento hacia atrás: tras un fallo, el niño ve el
 * ejemplo hecho y luego hace solo el último paso; con cada acierto, hace uno
 * más, hasta que lo hace entero; y entonces la cuenta se le pregunta sin
 * pasos, como a cualquiera. Un acierto sin ayuda con ningún fallo reciente
 * también quita la ayuda: seguir dando pasos a quien ya no los necesita
 * estorba, que es el «efecto de inversión de la pericia» de Kalyuga y Sweller.
 *
 * La primera vez que sale una destreza no hay ayuda: se prueba, y si sale
 * bien, no había nada que enseñar. La ayuda está siempre a un botón de
 * distancia, y eso ya es suficiente para no dejar a nadie atascado.
 */
export function ayudaPara(
  destreza: string,
  historial: readonly { destreza: string; ok: boolean; ayuda?: boolean }[],
  preguntas: number,
): Ayuda {
  const sinAyuda: Ayuda = { ejemploAntes: false, pasosQueHace: 'todos' }
  if (preguntas === 0) return sinAyuda
  const suyos = historial.filter((i) => i.destreza === destreza).slice(-VENTANA)
  if (suyos.length === 0) return sinAyuda

  const fallos = suyos.filter((i) => !i.ok).length
  const solos = suyos.filter((i) => i.ok && i.ayuda !== true).length
  const conAyuda = suyos.filter((i) => i.ok && i.ayuda === true).length
  if (fallos === 0 && solos >= 1) return sinAyuda

  // El fallo que abrió la ayuda no se descuenta: es la entrada. Los de después
  // sí, uno por uno: cada tropiezo devuelve un paso de ayuda.
  const hechos = Math.max(0, solos + conAyuda - Math.max(0, fallos - 1))
  const ultimo = suyos[suyos.length - 1]
  const pasosQueHace = 1 + hechos
  return {
    ejemploAntes: ultimo !== undefined && !ultimo.ok,
    pasosQueHace: pasosQueHace > preguntas ? 'todos' : pasosQueHace,
  }
}
